import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../common/hooks/hooks";
import { openGlobalModal } from "../../../../../common/features/globalModal/globalModalSlice";
import { setIsMailingSubscribed } from "../../checkoutSlice";
import Icon from "../../../../../common/components/Icon/Icon";

interface PlaceOrderActionsProps {
  onPlaceOrder: () => void;
  disabled: boolean;
  isLoading: boolean;
  selectedPaymentMethod: string | null;
}

const PlaceOrderActions: React.FC<PlaceOrderActionsProps> = ({
  onPlaceOrder,
  disabled,
  isLoading,
  selectedPaymentMethod,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const portalRoot = document.getElementById(
    "checkout-place-order-portal-root",
  );

  const isMailingSubscribed = useAppSelector(
    (state) => state.checkout.isMailingSubscribed,
  );
  const [isAgreementsChecked, setIsAgreementsChecked] = useState(false);

  // Access localized gateway constants
  const gatewayIds = window.commonVariables?.paymentGatewayIds;

  if (!portalRoot) return null;

  const handleOpenAgreements = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(
      openGlobalModal({
        contentKey: "legalAgreements",
        title: t("legalAgreements.modalTitle", "Sales Agreements"),
      }),
    );
  };

  const handlePlaceOrderClick = () => {
    if (!isAgreementsChecked) {
      toast.error(
        t(
          "legalAgreements.errorRequired",
          "Please accept the agreements to continue.",
        ),
      );
      return;
    }
    onPlaceOrder();
  };

  // --- Determine Button Content based on Gateway ---
  let buttonContent: React.ReactNode;
  let buttonModifierClass = "";

  const isCreditCardMethod =
    gatewayIds &&
    (selectedPaymentMethod === gatewayIds.IYZICO ||
      selectedPaymentMethod === gatewayIds.PAYTR);

  if (isCreditCardMethod) {
    buttonContent = (
      <>
        <Icon iconName="credit-card-security" className="icon--filled" />
        <span>{t("checkoutForm.actions.pay", "Pay")}</span>
      </>
    );
  } else if (gatewayIds && selectedPaymentMethod === gatewayIds.BACS) {
    buttonModifierClass = "place-order-actions__button--place-order";
    buttonContent = (
      <span>{t("checkoutForm.actions.placeOrder", "Place Order")}</span>
    );
  } else {
    buttonModifierClass = "place-order-actions__button--default";
    buttonContent = (
      <span>{t("checkoutForm.actions.placeOrder", "Place Order")}</span>
    );
  }

  return ReactDOM.createPortal(
    <div className="place-order-actions">
      {/* --- MAILING CONSENT CHECKBOX --- */}
      <div className="checkout-mailing-consent form-field checkbox-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={isMailingSubscribed}
            onChange={(e) => dispatch(setIsMailingSubscribed(e.target.checked))}
          />
          <span className="checkbox-text">
            {t(
              "mailingConsent.checkboxLabel",
              "I want to receive news and exclusive offers via email.",
            )}
          </span>
        </label>
      </div>

      {/* --- AGREEMENTS CHECKBOX --- */}
      <div className="checkout-agreements form-field checkbox-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={isAgreementsChecked}
            onChange={(e) => setIsAgreementsChecked(e.target.checked)}
          />
          <span className="checkbox-text">
            <a href="#" onClick={handleOpenAgreements}>
              {t(
                "legalAgreements.checkboxText",
                "I have read and agree to the Preliminary Information Form and Distance Sales Agreement.",
              )}
            </a>
          </span>
        </label>
      </div>

      {/* --- PLACE ORDER BUTTON --- */}
      <button
        className={`place-order-actions__button ${buttonModifierClass}`}
        onClick={handlePlaceOrderClick}
        disabled={disabled || isLoading}
      >
        {isLoading ? (
          <>
            <span className="loader-spinner"></span>
            <span>{t("checkoutForm.actions.processing", "Processing...")}</span>
          </>
        ) : (
          buttonContent
        )}
      </button>

      <div className="place-order-actions__secure-notice">
        <Icon iconName="lock-closed" className="icon--stroke-thick" />
        <span>{t("placeOrderButton.secureCheckout", "Secure Checkout")}</span>
      </div>
    </div>,
    portalRoot,
  );
};

export default PlaceOrderActions;
